import React from "react";
import { motion } from "framer-motion";
import { useLang } from "../i18n/LanguageContext";
import { ArrowRight, UserPlus, Hash, PackageCheck } from "lucide-react";

const PROFILE_LINK = "https://trackmypackage.app/join/shipcostarica";
const ICONS = [UserPlus, Hash, PackageCheck];

const HowItWorks = () => {
  const { t } = useLang();

  return (
    <section id="how" data-testid="how-section" className="relative py-24 sm:py-32 bg-brand-cream overflow-hidden">
      {/* Decorative beach band */}
      <div
        className="absolute inset-x-0 bottom-0 h-32 opacity-25"
        style={{
          background: "linear-gradient(180deg, transparent 0%, #E3C16F 60%, #1D8B9B 100%)",
        }}
        aria-hidden="true"
      />
      <div className="absolute -top-10 -left-10 w-72 h-72 rounded-full bg-brand-red/10 blur-3xl" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 mb-6 justify-center">
            <span className="h-px w-8 bg-brand-red" />
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-brand-red">
              {t.how.eyebrow}
            </span>
            <span className="h-px w-8 bg-brand-red" />
          </div>
          <h2
            data-testid="how-headline"
            className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-[-0.02em] text-brand-navy leading-[1.05]"
          >
            {t.how.title}
          </h2>
          <p className="mt-5 text-base sm:text-lg text-brand-navy/75 leading-relaxed max-w-xl mx-auto">
            {t.how.sub}
          </p>
        </div>

        {/* 3 step cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
          {t.how.steps.map((s, i) => {
            const Icon = ICONS[i];
            const isMiddle = i === 1;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                data-testid={`how-step-${i}`}
                className={`relative rounded-3xl p-7 sm:p-8 border transition-all duration-300 hover:-translate-y-1 ${
                  isMiddle
                    ? "bg-brand-navy text-brand-cream border-brand-navy hover:shadow-[0_25px_50px_-15px_rgba(14,42,86,0.5)]"
                    : "bg-white text-brand-navy border-brand-navy/10 hover:shadow-[0_20px_40px_-15px_rgba(14,42,86,0.25)]"
                }`}
              >
                {/* Step number */}
                <div
                  className={`absolute top-6 right-7 font-heading font-black text-5xl tracking-tighter leading-none ${
                    isMiddle ? "text-brand-cream/15" : "text-brand-navy/10"
                  }`}
                >
                  0{i + 1}
                </div>

                <div
                  className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 ${
                    isMiddle ? "bg-brand-red text-white" : "bg-brand-cream text-brand-red"
                  }`}
                >
                  <Icon size={26} strokeWidth={2.2} />
                </div>

                <div
                  className={`text-[11px] font-black uppercase tracking-[0.2em] mb-2 ${
                    isMiddle ? "text-brand-red" : "text-brand-red"
                  }`}
                >
                  {t.how.stepLabel} {i + 1}
                </div>

                <h3
                  className={`font-heading font-bold text-xl sm:text-[22px] tracking-tight leading-snug mb-3 ${
                    isMiddle ? "text-brand-cream" : "text-brand-navy"
                  }`}
                >
                  {s.t}
                </h3>
                <p
                  className={`text-sm sm:text-base leading-relaxed ${
                    isMiddle ? "text-brand-cream/85" : "text-brand-navy/75"
                  }`}
                >
                  {s.d}
                </p>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-14 text-center">
          <a
            href={PROFILE_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="how-cta"
            className="group inline-flex items-center gap-3 rounded-full bg-brand-red text-white font-heading font-bold text-base sm:text-lg px-8 py-4 shadow-[0_18px_40px_-12px_rgba(216,35,42,0.65)] hover:bg-brand-navy hover:shadow-[0_18px_40px_-12px_rgba(14,42,86,0.7)] transition-all"
          >
            {t.how.cta}
            <ArrowRight size={20} className="group-hover:translate-x-1.5 transition-transform" />
          </a>
          <div className="mt-4 text-sm text-brand-navy/60 font-medium">{t.how.footnote}</div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
